import { useNote } from '@payaca/store/hooks/appState';
import moment from 'moment-timezone';
import { useMemo } from 'react';

import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { getNoteTitle } from '@payaca/helpers/noteHelper';
import { isValidHtml, parseHtmlToReact } from '@payaca/utilities/htmlUtilities';
import { useSelector } from '../../../api/state';
import { useAccount, useAccountUser } from '../../../utils/storeHooks';

import MarkdownLabel from '@payaca/components/markdownLabel/MarkdownLabel';
import QuickActionsElement, {
  QuickActionConfig,
} from '@payaca/components/quickActionsTableCell/QuickActionsElement';

import { Note } from '@payaca/types/notesTypes';

import './NoteControl.sass';

type Props = {
  noteId: number;
  onPersistNoteSuccess: () => void;
  editNote: (note: Note) => void;
  archiveNote: (noteId: number) => void;
};
const NoteControl = ({ archiveNote, editNote, noteId }: Props) => {
  const note = useNote(noteId);
  const account = useAccount();
  const profile = useSelector((state: any) => state.users.myProfile);
  const createdByUser = useAccountUser(note?.createdByUserId);

  const timestampShortDateRegionalFormat = useMemo(
    () =>
      getInternationalMomentDateFormatByRegion(
        DateFormats.TIMESTAMP_SHORT,
        account.region
      ),
    [account]
  );

  const canEditNote = useMemo(
    () => note && note.createdByUserId === profile.id,
    [note, profile.id]
  );

  const quickActions = useMemo(() => {
    const quickActions: QuickActionConfig[] = [];
    if (canEditNote) {
      quickActions.push({
        actionName: 'Edit',
        actionBehaviour: () => {
          if (note) {
            editNote(note);
          }
        },
      });
    }
    quickActions.push({
      actionName: 'Delete',
      actionBehaviour: () => archiveNote(noteId),
    });
    return quickActions;
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'archiveNote', and 'canEditNote'
  }, [editNote, note, noteId]);

  const noteTitle = useMemo(
    () => getNoteTitle(note, createdByUser),
    [note, createdByUser]
  );

  if (!note) {
    return null;
  }
  return (
    <div className="note-control flex-container flex-center">
      <div
        className={`note-control-panel flex-center grow${
          canEditNote ? ' clickable' : ''
        }`}
        onClick={canEditNote ? () => editNote(note) : undefined}
      >
        <>
          <div className="note-header">
            <div>
              <strong>{noteTitle}</strong>
              {createdByUser &&
                `by ${createdByUser?.firstname} ${createdByUser?.lastname}`}
            </div>
            <small className="timestamp">
              {moment(note.createdAt).format(timestampShortDateRegionalFormat)}
            </small>
          </div>
          {isValidHtml(note.description) ? (
            parseHtmlToReact(note.description)
          ) : (
            <MarkdownLabel markdown={note.description} />
          )}
        </>
      </div>
      <QuickActionsElement
        recordId={noteId}
        quickActions={quickActions}
        renderDisabledIfNoActions={true}
      />
    </div>
  );
};

export default NoteControl;
